import React, { useState, useEffect, useRef, useCallback } from "react";
import io from "socket.io-client";
import { StopCircle, Mic } from "lucide-react";
import dashboardService from "../../api";

const TranscriptionWidget = ({ onTranscriptionComplete }) => {
  // State variables
  const [isRecording, setIsRecording] = useState(false);
  const [hasStartedRecording, setHasStartedRecording] = useState(false);
  const [transcription, setTranscription] = useState("");
  const [persistentLines, setPersistentLines] = useState([]);
  const [uploadStatus, setUploadStatus] = useState("");
  
  // Refs
  const transcriptionEndRef = useRef(null);
  const transcriptionTextRef = useRef("");
  const lastFinalTextRef = useRef("");
  const audioContextRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const processorRef = useRef(null);
  const intervalRef = useRef(null);
  const socketRef = useRef(null);
  const fileCountRef = useRef(0);

  // Auto-scroll to bottom when transcription updates
  useEffect(() => {
    if (transcriptionEndRef.current) {
      transcriptionEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [persistentLines, transcription]);

  // Initialize socket connection
  useEffect(() => {
    const socketUrl = dashboardService.getTranscriptionSocketUrl();
    
    socketRef.current = io(socketUrl, {
      path: '/socket.io',
      transports: ["websocket", "polling"],
      reconnectionAttempts: 5
    });
    
    const socket = socketRef.current;
    
    socket.on("transcription", (data) => {
      if (!data || !data.text) return;

      if (data.text.startsWith("##")) {
        const cleanText = data.text.slice(2).trim();
        transcriptionTextRef.current += `\n${cleanText}`;
        setPersistentLines((prev) => [...prev, data.text]);
        setTranscription("");
      } else if (data.text.startsWith(">>")) {
        setTranscription(data.text);
      }
    });
    
    socket.on("connect_error", (error) => {
      console.error("❌ Socket connection error:", error);
    });
    
    return () => {
      socket.disconnect();
    };
  }, []);

  // Start recording handler
  const handleStartRecording = useCallback(async () => {
    if (isRecording) return;
    
    setIsRecording(true);
    setHasStartedRecording(true);
    setUploadStatus("");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      // Load the audio worklet
      await audioContext.audioWorklet.addModule('/audio-processor.js');

      const source = audioContext.createMediaStreamSource(stream);
      const workletNode = new AudioWorkletNode(audioContext, 'audio-processor');

      workletNode.port.onmessage = (event) => {
        try {
          const inputData = event.data.audioData;
          console.log("Audio data received:", {
            length: inputData.length,
            sampleMin: Math.min(...inputData),
            sampleMax: Math.max(...inputData),
            sampleRate: audioContext.sampleRate
          });
          
          const originalSampleRate = audioContext.sampleRate;
          const targetSampleRate = 16000;
          const downsampled = dashboardService.downsampleBuffer(inputData, originalSampleRate, targetSampleRate);
          const pcmBuffer = dashboardService.floatTo16BitPCM(downsampled);
          const binaryData = new Uint8Array(pcmBuffer);
          
          console.log("Sending audio chunk:", {
            originalLength: inputData.length,
            downsampledLength: downsampled.length,
            binaryLength: binaryData.length,
            firstFewBytes: Array.from(binaryData.slice(0, 10))
          });
          
          socketRef.current.emit("audio_stream", binaryData);
        } catch (err) {
          console.error("❌ Error during audio processing:", err);
        }
      };

      source.connect(workletNode);
      workletNode.connect(audioContext.destination);
      processorRef.current = workletNode;
    } catch (error) {
      console.error("❌ Error accessing microphone:", error);
      setIsRecording(false);
    }

    intervalRef.current = setInterval(() => {
      const currentText = (transcriptionTextRef.current + (transcription || ""))
        || lastFinalTextRef.current;

      if (currentText.trim()) {
        socketRef.current.emit("context_update", { text: currentText });
      }

      if (onTranscriptionComplete) {
        onTranscriptionComplete(currentText);
      }
    }, 3000);
  }, [isRecording, transcription, onTranscriptionComplete]);

  // Stop recording handler
  const handleStopRecording = useCallback(async () => {
    if (!isRecording) return;
    
    setIsRecording(false);

    if (transcription.trim() && !transcription.startsWith(">>")) {
      transcriptionTextRef.current += `\n${transcription}`;
    }

    const finalText = transcriptionTextRef.current;

    if (onTranscriptionComplete) {
      onTranscriptionComplete(finalText);
    }

    lastFinalTextRef.current = finalText;

    // Upload transcription
    if (finalText.trim()) {
      setUploadStatus("Uploading...");
      const result = await dashboardService.uploadTranscription(finalText);
      
      if (result.success) {
        setUploadStatus("Upload complete");
      } else {
        setUploadStatus(`Upload failed: ${result.error}`);
      }
      
      setTimeout(() => setUploadStatus(""), 3000);
    }

    transcriptionTextRef.current = "";

    // Clean up audio resources
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current.port.postMessage({ terminate: true });
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
    }

    if (audioContextRef.current) {
      audioContextRef.current.close().then(() => console.log("Audio context closed"));
    }

    socketRef.current.emit("stop_audio");

    clearInterval(intervalRef.current);
    intervalRef.current = null;
  }, [isRecording, transcription, onTranscriptionComplete]);

  return (
    <div className="bg-white p-4 shadow-md shadow-blue-900/5 max-w-full relative rounded-xl">
      <h3 className="font-semibold my-2">Live Transcription</h3>
      <div className="bg-gray-50 border border-gray-200 rounded-md p-3 h-[200px] overflow-y-auto flex flex-col justify-between">
        <div className="flex-grow overflow-y-auto">
          {persistentLines.map((line, index) => (
            <p 
              key={`persistent-${index}-transcription`} 
              className="my-1 leading-normal text-sm text-gray-700"
            >
              {line}
            </p>
          ))}
          
          {transcription && (
            <p 
              key="interim-transcription" 
              className="my-1 leading-normal text-sm text-gray-700"
            >
              {transcription}
            </p>
          )}
          
          {uploadStatus && (
            <p 
              className={`text-xs italic ${
                uploadStatus.includes("failed") ? "text-red-500" : "text-green-500"
              }`}
            >
              {uploadStatus}
            </p>
          )}
          
          <div ref={transcriptionEndRef} />
        </div>

        <div className="mt-3 flex justify-end">
          <button
            className={`flex items-center justify-center ${
              isRecording
                ? "bg-red-100 text-red-700 hover:bg-red-200"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            } border-none rounded-md py-2 px-3 text-sm font-medium cursor-pointer transition-all duration-200`}
            onClick={isRecording ? handleStopRecording : handleStartRecording}
          >
            {isRecording ? (
              <>
                <StopCircle size={16} className="mr-1.5" />
                Stop
              </>
            ) : (
              <>
                <Mic size={16} className="mr-1.5" />
                Record
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(TranscriptionWidget);